import { parseISO, isValid, format, differenceInHours, differenceInMinutes, differenceInCalendarMonths, addMonths, parse } from 'date-fns';
import { id, enUS } from 'date-fns/locale';
const availableLocales = {
  id: id,
  en: enUS
};
class Formatter {
  static formatDate(date) {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return isValid(dateObj) ? format(dateObj, 'yyyy-MM-dd HH:mm:ss') : 'Invalid date';
  }
  static formatDateWithCustomFormat(date, formatString, lang = 'id') {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    const locale = availableLocales[lang] || id;
    return isValid(dateObj) ? format(dateObj, formatString, {
      locale
    }) : 'Invalid date';
  }
  static calculateTimeDifference(startHour, endHour) {
    const start = typeof startHour === 'string' ? parseISO(startHour) : startHour || new Date();
    const end = typeof endHour === 'string' ? parseISO(endHour) : endHour || new Date();
    const hours = differenceInHours(end, start);
    const minutes = differenceInMinutes(end, start) % 60;
    return `${hours} Hours, ${minutes} Minutes`;
  }
  static calculateAge(birthDate, targetDate) {
    const target = typeof targetDate === 'string' ? parseISO(targetDate) : targetDate;
    const birth = typeof birthDate === 'string' ? parseISO(birthDate) : birthDate;
    if (!isValid(target) || !isValid(birth)) {
      return 'Invalid date(s)';
    }
    const monthsDiff = differenceInCalendarMonths(target, birth);
    const years = Math.floor(monthsDiff / 12);
    const months = monthsDiff % 12;
    const daysInTargetMonth = addMonths(birth, monthsDiff).getDate() - birth.getDate();
    return `${years} years ${months} months ${daysInTargetMonth} days`;
  }
  static formatCurrency(amount, currencySymbol) {
    const number = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(number)) return 'Invalid amount';
    return currencySymbol + ' ' + number.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, '$1,');
  }
  static generateFutureOrPastMonthList(startDate, monthCount = 12) {
    const months = [];
    const increment = monthCount > 0 ? 1 : -1;
    for (let i = 1; i <= Math.abs(monthCount); i++) {
      const newDate = addMonths(startDate, i * increment);
      months.push(format(newDate, 'MMMM yyyy'));
    }
    return months;
  }
  static formatMonthYear(date, formatOutput) {
    return format(parse(date, 'MMMM yyyy', new Date()), formatOutput);
  }
  static formatPrice(value) {
    const valueStr = String(value);
    const [integerPart, decimalPart] = valueStr.split('.');
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
  }
  static toLocaleCurrency(amount) {
    return amount.toLocaleString('id-ID', {
      style: 'currency',
      currency: 'IDR',
      localeMatcher: 'lookup'
    });
  }
  static formatWithCustomSeparator(amount, separator = '.') {
    const formattedAmount = new Intl.NumberFormat('id-ID', {
      maximumSignificantDigits: 3
    }).format(amount);
    return formattedAmount.replace(/\./g, separator);
  }
  static dateToTimestamp(date) {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return isValid(dateObj) ? dateObj.getTime().toString() : 'Invalid date';
  }
  static dateToUnixTimestamp(date) {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return isValid(dateObj) ? Math.floor(dateObj.getTime() / 1000).toString() : 'Invalid date';
  }
  static getUnixTimestamp(date) {
    const dateObj = parseISO(date);
    return isValid(dateObj) ? Math.floor(dateObj.getTime() / 1000).toString() : 'Invalid date';
  }
  static getDayName(date, lang = 'id') {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    const locale = availableLocales[lang] || id;
    return isValid(dateObj) ? format(dateObj, 'EEEE', {
      locale
    }) : 'Invalid date';
  }
  static getFormattedDateTime(date, lang = 'id') {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    const locale = availableLocales[lang] || id;
    return isValid(dateObj) ? format(dateObj, 'PP', {
      locale
    }) : 'Invalid date';
  }
  static getCustomFormattedDateTime(date, lang = 'id') {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    const locale = availableLocales[lang] || id;
    return isValid(dateObj) ? format(dateObj, 'EEEE, do-MM-yyyy', {
      locale
    }) : 'Invalid date';
  }
  static getHours(date, lang = 'id') {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    const locale = availableLocales[lang] || id;
    return isValid(dateObj) ? format(dateObj, 'p', {
      locale
    }) : 'Invalid date';
  }
  static calculateDaysDifference(start, end) {
    const startDate = typeof start === 'string' ? parseISO(start) : start;
    const endDate = typeof end === 'string' ? parseISO(end) : end;
    if (!isValid(startDate) || !isValid(endDate)) return NaN;
    return Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  }
}

/*
 * Public API Surface of utils.formatter
 */

/**
 * Generated bundle index. Do not edit.
 */

export { Formatter };
